import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridFilterChangeEvent, GridCellProps } from '@progress/kendo-react-grid';
import ErrorMessage from '@components-clean/core/error';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import DeleteFunctionalDataReviewDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/delete-functional-data-review-dialog';
import UpdateFunctionalDataReviewDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/update-functional-data-review-dialog';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType, NamedItemType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import { FunctionalDataReviewsIncludingCtQfDetailFields } from '@queries/generated/fragments.generated';
import {
    getFunctionalDataReviewsByStudyAndCtq,
    getFunctionalDataReviewsByStudyAndCtqVariables,
    getFunctionalDataReviewsByStudyAndCtq_OpraQueries_functionalDataReviewsByStudyAndCtq_FunctionalDataReview as FdrNode,
} from '@queries/generated/risk-assessment.generated';
import { GET_FUNCTIONAL_DATA_REVIEWS_BY_STUDY_AND_CTQ } from '@queries/risk-assessment';

type FdrRow = {
    id: string;
    recordId: string;
    responsibleFunction: INamedItem | null;
    description: string;
    comment: string;
    created?: string;
    updated?: string;
    createdByFullName?: string;
    updatedByFullName?: string;
};

function toStringSafe(value: unknown): string {
    return value == null ? '' : String(value);
}

function toNamedItem(source: FdrNode['responsibleFunction']): INamedItem | null {
    if (!source) return null;
    return {
        id: toStringSafe(source.id),
        name: source.name ?? '',
        active: source.active !== false,
    };
}

const FunctionalDataReviewsGrid: React.FC = () => {
    const { t } = useTranslation('ram', { keyPrefix: 'ctqf.functionalDataReviews.grid' });
    const { current, setCurrent } = useContext(CTQFactorsContext);
    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();

    const user = useAuthorization();
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const { study } = current;
    const ctqId = current.selectionState.selectedItem?.id ?? '';

    const { loading, error, data, refetch } = useQuery<
        getFunctionalDataReviewsByStudyAndCtq,
        getFunctionalDataReviewsByStudyAndCtqVariables
    >(GET_FUNCTIONAL_DATA_REVIEWS_BY_STUDY_AND_CTQ, {
        variables: { studyId: study?.id ?? '', ctqId },
        skip: !ctqId,
    });

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } =
        useQuery<getNamedItemsByTypeForStudy>(GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY, {
            variables: {
                studyId: study?.id,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        });

    const [rows, setRows] = useState<FdrRow[]>([]);
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const [selectedFdr, setSelectedFdr] = useState<FdrNode | null>(null);
    const [showEdit, setShowEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    useEffect(() => {
        const source = data?.functionalDataReviewsByStudyAndCtq ?? [];
        const compact: FdrNode[] = source.filter((x): x is FdrNode => x !== null);

        const mapped: FdrRow[] = compact.map((x) => ({
            id: toStringSafe(x.id),
            recordId: toStringSafe(x.recordId),
            responsibleFunction: toNamedItem(x.responsibleFunction),
            description: x.description ?? '',
            comment: x.comment ?? '',
            created: x.created ? String(x.created) : undefined,
            updated: x.updated ? String(x.updated) : undefined,
            createdByFullName: x.createdBy?.fullName ?? '',
            updatedByFullName: x.updatedBy?.fullName ?? '',
        }));

        if (!current.functionalDataReviewsGridIsFiltered) {
            setFilterState({ logic: 'and', filters: [] });
            setCurrent((prev) => ({ ...prev, functionalDataReviewsGridIsFiltered: false }));
        }

        setRows(mapped);
    }, [data, setCurrent]);

    const exportFileName = useMemo(() => {
        const ctq = (current.selectionState.selectedItem?.description ?? '').substring(0, 80);
        const studyNumber = study?.studyNumber ?? '';
        const date = stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY');
        return `${ctq}-Functional Reviews_${studyNumber}_${date}`;
    }, [current, study]);

    const columns: GridColumnProps[] = [
        {
            id: 'description',
            field: 'description',
            title: t('columns.fdr.columns.description'),
            minWidth: 300,
            filter: 'text',
            cells: { data: (p: GridCellProps) => TooltipCell({ isCsv: false })(p) },
            showByDefault: true,
        },
        {
            id: 'responsibleFunction',
            field: 'responsibleFunction',
            title: t('columns.fdr.columns.responsibleFunction'),
            minWidth: 220,
            filter: 'text',
            filterKey: 'name',
            lovData: responsibleFunctionsData?.namedItemsByTypeForStudy
                ? responsibleFunctionsData.namedItemsByTypeForStudy
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (v: INamedItem) => v?.active === false,
                    renderValue: (v: INamedItem) => v?.name,
                }),
            },
            showByDefault: true,
        },
        {
            id: 'comment',
            field: 'comment',
            title: t('columns.fdr.columns.comment'),
            minWidth: 300,
            filter: 'text',
            cells: { data: (p: GridCellProps) => TooltipCell({ isCsv: false })(p) },
            showByDefault: true,
        },
        {
            id: 'createdByFullName',
            field: 'createdByFullName',
            title: t('columns.fdr.columns.createdBy'),
            minWidth: 150,
            filter: 'text',
            showByDefault: false,
        },
        {
            id: 'created',
            field: 'created',
            title: t('columns.fdr.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
        },
        {
            id: 'updatedByFullName',
            field: 'updatedByFullName',
            title: t('columns.fdr.columns.updatedBy'),
            minWidth: 150,
            filter: 'text',
            showByDefault: false,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('columns.fdr.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
        },
        {
            id: 'recordId',
            field: 'recordId',
            title: t('columns.fdr.columns.id'),
            minWidth: 160,
            filter: 'text',
            showByDefault: false,
        },
    ];

    const sortedRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const descCompare = (a.description ?? '').localeCompare(b.description ?? '');
            if (descCompare !== 0) return descCompare;

            const aName = a.responsibleFunction?.name ?? '';
            const bName = b.responsibleFunction?.name ?? '';
            return aName.localeCompare(bName);
        });
    }, [rows]);

    const filterChanged = async (event: GridFilterChangeEvent): Promise<void> => {
        const hasFiltersApplied = event.filter ? event.filter.filters.length > 0 : false;
        setCurrent((prev) => ({ ...prev, functionalDataReviewsGridIsFiltered: hasFiltersApplied }));
        setFilterState(event.filter as CompositeFilterDescriptor);
    };

    if (error) return <ErrorMessage />;
    if (ctqId && (!data || loadingResponsibleFunctions || !responsibleFunctionsData)) return <Loading />;

    return (
        <>
            <Grid
                excludeHiddenRowMenuActions
                title={t('title')}
                maxHeight="auto"
                testDataId="ctqf-fdr-grid"
                data={sortedRows}
                columns={columns}
                loading={loading}
                useLoadingOverlay
                showAddIcon={false}
                scrollable="virtual"
                dataItemKey="id"
                rowMenuActions={[
                    {
                        id: 1,
                        label: 'Edit Comment',
                        onClick: (row: unknown) => {
                            setSelectedFdr(row as FdrNode);
                            setShowEdit(true);
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'] as IconProp,
                    },
                    {
                        id: 2,
                        label: 'Delete Functional Data Review',
                        onClick: (row: unknown) => {
                            setSelectedFdr(row as FdrNode);
                            setShowDelete(true);
                        },
                        disabled: !canEdit,
                        icon: ['fas', 'trash-xmark'] as IconProp,
                        colorProp: 'Red',
                    },
                ]}
                filter={filterState}
                onFilterChange={filterChanged}
                clearFilter={() => {
                    setCurrent((prev) => ({ ...prev, functionalDataReviewsGridIsFiltered: false }));
                    setFilterState({ logic: 'and', filters: [] });
                    return Promise.resolve();
                }}
                exportFilename={exportFileName}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                persistentStorageKey={String(PersistentStorageKeys['ctq-fdr-child-grid'])}
            />

            {showEdit && selectedFdr && (
                <UpdateFunctionalDataReviewDialog
                    onClose={async () => {
                        setShowEdit(false);
                        await refetch();
                    }}
                    fdr={selectedFdr as unknown as FunctionalDataReviewsIncludingCtQfDetailFields}
                />
            )}

            {showDelete && selectedFdr && (
                <DeleteFunctionalDataReviewDialog
                    onClose={async () => {
                        setShowDelete(false);
                        await refetch();
                    }}
                    fdr={selectedFdr as unknown as FunctionalDataReviewsIncludingCtQfDetailFields}
                />
            )}
        </>
    );
};

export default FunctionalDataReviewsGrid;
